import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO/SEO'
import '../styles/pages/PortfolioPage.css'

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('all')

  // Solution showcase - what we can build for each industry
  const solutions = [
    {
      id: 1,
      title: 'Digital Banking Platform',
      category: 'fintech',
      industry: 'Financial Services',
      description: 'Mobile-first digital banking platforms with real-time transactions, biometric authentication, and AI-powered fraud detection.',
      technologies: ['React Native', 'Node.js', 'MongoDB', 'AI/ML'],
      capability: 'End-to-end fintech development',
      color: '#4F6FFF'
    },
    {
      id: 2,
      title: 'Hospital Management System',
      category: 'healthcare',
      industry: 'Healthcare',
      description: 'Integrated HMS solutions connecting multiple facilities with centralized patient records and telemedicine capabilities.',
      technologies: ['React', 'Python', 'PostgreSQL', 'AWS'],
      capability: 'Healthcare digitization',
      color: '#1FCF9F'
    },
    {
      id: 3,
      title: 'E-Learning Platform',
      category: 'education',
      industry: 'Education',
      description: 'Custom LMS with live classes, automated grading, plagiarism detection, and student analytics.',
      technologies: ['React', 'Django', 'WebRTC', 'Redis'],
      capability: 'EdTech solutions',
      color: '#FFD93D'
    },
    {
      id: 4,
      title: 'Smart Farm Management',
      category: 'agriculture',
      industry: 'Agriculture',
      description: 'IoT-powered farm management with real-time soil monitoring, automated irrigation, and yield prediction.',
      technologies: ['IoT', 'React', 'Python', 'TensorFlow'],
      capability: 'AgriTech innovation',
      color: '#4ECDC4'
    },
    {
      id: 5,
      title: 'E-Commerce Marketplace',
      category: 'ecommerce',
      industry: 'Retail',
      description: 'Multi-vendor marketplaces with payment integration, logistics tracking, and vendor analytics dashboards.',
      technologies: ['Next.js', 'Node.js', 'Stripe', 'MongoDB'],
      capability: 'E-commerce development',
      color: '#FF6B6B'
    },
    {
      id: 6,
      title: 'Driving School Digitization',
      category: 'government',
      industry: 'Government & Compliance',
      description: 'End-to-end digitization of driving school operations: enrollment, records, FRSC compliance, and verification.',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Cloud'],
      capability: 'Compliance automation',
      color: '#B19CD9'
    },
    {
      id: 7,
      title: 'Real Estate Portal',
      category: 'realestate',
      industry: 'Real Estate',
      description: 'Property listing platforms with virtual tours, agent management, and automated valuation models.',
      technologies: ['React', 'Express', 'MongoDB', 'Mapbox'],
      capability: 'PropTech solutions',
      color: '#FFDAB9'
    },
    {
      id: 8,
      title: 'NGO Impact Dashboard',
      category: 'nonprofit',
      industry: 'Non-Profit',
      description: 'Real-time donor reporting dashboards with M&E data collection, impact visualization, and compliance tracking.',
      technologies: ['React', 'D3.js', 'Python', 'AWS'],
      capability: 'NGO digital systems',
      color: '#A8E6CF'
    },
    {
      id: 9,
      title: 'POS & Inventory System',
      category: 'ecommerce',
      industry: 'Retail Operations',
      description: 'Point-of-sale systems with real-time inventory tracking, multi-branch sync, and sales analytics.',
      technologies: ['React', 'Node.js', 'MySQL', 'Electron'],
      capability: 'Retail automation',
      color: '#FFB347'
    }
  ]

  const categories = [
    { id: 'all', label: 'All Solutions' },
    { id: 'fintech', label: 'FinTech' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'education', label: 'Education' },
    { id: 'ecommerce', label: 'E-Commerce' },
    { id: 'agriculture', label: 'AgriTech' },
    { id: 'government', label: 'Government' },
    { id: 'realestate', label: 'Real Estate' },
    { id: 'nonprofit', label: 'Non-Profit' }
  ]

  const filtered = activeFilter === 'all'
    ? solutions
    : solutions.filter(p => p.category === activeFilter)

  return (
    <div className="portfolio-page">
      <SEO
        title="Solutions - FIGO Digital Services"
        description="Explore our digital solutions for software development, AI automation, and digital transformation across industries in Nigeria and beyond."
        keywords="FIGO solutions, software development, digital transformation, industry solutions"
        url="https://figodigitalservices.com/portfolio"
      />

      {/* Hero */}
      <section className="portfolio-hero">
        <div className="portfolio-hero-container">
          <motion.span
            className="portfolio-badge"
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
          >
            What We Build
          </motion.span>
          <motion.h1
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Solutions That <span className="gradient-text">Transform Industries</span>
          </motion.h1>
          <motion.p
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            From fintech platforms to hospital systems, we build digital products designed to transform operations and drive growth.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="portfolio-filters">
        <div className="filters-container">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`filter-btn ${activeFilter === cat.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="portfolio-grid-section">
        <div className="portfolio-grid-container">
          <motion.div className="portfolio-grid" layout>
            {filtered.map((solution, index) => (
              <motion.div
                key={solution.id}
                className="portfolio-card"
                layout
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                exit={{ y: 20 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <div className="card-accent" style={{ background: solution.color }} />
                <div className="card-body">
                  <div className="card-top">
                    <span className="card-category">{categories.find(c => c.id === solution.category)?.label}</span>
                    <span className="card-client">{solution.industry}</span>
                  </div>
                  <h3 className="card-title">{solution.title}</h3>
                  <p className="card-description">{solution.description}</p>
                  <div className="card-technologies">
                    {solution.technologies.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                  <div className="card-result">
                    <span className="result-icon">&#10003;</span>
                    <span className="result-text">{solution.capability}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="portfolio-cta">
        <div className="portfolio-cta-container">
          <h2>Have a Project in Mind?</h2>
          <p>Let's build something that transforms your operations and drives growth.</p>
          <Link to="/contact" className="portfolio-cta-btn">Start Your Project</Link>
        </div>
      </section>
    </div>
  )
}

export default PortfolioPage
